import axios from "axios";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useGlobalMessage } from "src/shared/context/globalMessage";
import { useSessionTimer } from "src/shared/context/sessionContext";
import { useAuth } from "../../context/useAuth";
import { queryClient } from "../QeuryClient/reactQueryClient";
import { USER_DATA_INFO_QUERY_KEY } from "src/shared/enums/useQueryKeys";
import { getTraceData } from "@sentry/core";

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
  withCredentials: true,
});

const configureHttpClient = () => {
  const { resetSessionTimer } = useSessionTimer();
  const { i18n } = useTranslation();
  const {
    setMaintenance,
    infoMessage,
    setInfoMessage,
    chamberMessage,
    setChamberMessage,
  } = useGlobalMessage();

  const { setUserCantAccess, setSessionTimedOut } = useAuth();

  httpClient.interceptors.response.use(
    (response) => {
      const headers = response.headers;

      if (headers["x-maintenance-mode-on"]) {
        setMaintenance(headers["x-maintenance-mode-on"]);
      } else {
        setMaintenance(null);
      }

      if (headers["x-user-info-message-on"]) {
        setInfoMessage({
          ...infoMessage,
          date: format(
            new Date(headers["x-user-info-message-on"]),
            "yyyy-MM-dd HH:mm:ss",
          ),
        });
      } else {
        setInfoMessage({
          message: null,
          date: null,
        });
      }

      if (headers["x-chamber-info-message-on"]) {
        setChamberMessage({
          ...chamberMessage,
          date: format(
            new Date(headers["x-chamber-info-message-on"]),
            "yyyy-MM-dd HH:mm:ss",
          ),
        });
      } else {
        setChamberMessage({
          message: null,
          date: null,
        });
      }
      resetSessionTimer();
      return response;
    },
    (error) => {
      if (
        (error?.response?.status == 502 || error?.response?.status == 503) &&
        window.location.pathname !== "/error502"
      ) {
        window.location.href = import.meta.env.VITE_APP_URL + "/error502";
        return Promise.reject({ ...error, response: { data: undefined } });
      } else if (error?.response?.status == 504) {
        let text = "Σφάλμα συστήματος, προσπαθήστε ξανά αργότερα.";
        if (localStorage.getItem("lng")) {
          switch (localStorage.getItem("lng")) {
            case "el":
              text = "Σφάλμα συστήματος, προσπαθήστε ξανά αργότερα.";
              break;
            case "en":
              text = "System error, try again later.";
              break;
            default:
              text = "Σφάλμα συστήματος, προσπαθήστε ξανά αργότερα.";
          }
        }
        return Promise.reject({ ...error, response: { data: text } });
      } else if (error?.response?.status == 401) {
        queryClient.invalidateQueries(USER_DATA_INFO_QUERY_KEY);
        setSessionTimedOut(true);
      } else if (error?.response?.status == 403) {
        setUserCantAccess(true);
      }

      return Promise.reject(error);
    },
  );

  httpClient.interceptors.request.use(
    (config) => {
      config.params = { ...config.params, lang: i18n?.language ?? "el" };

      const traceData = getTraceData();

      if (traceData["sentry-trace"]) {
        config.headers["sentry-trace"] = traceData["sentry-trace"];
      }

      if (traceData.baggage) {
        config.headers["baggage"] = traceData["baggage"];
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );
};
export { configureHttpClient, httpClient };
